import { BuildClass, timer } from '../../../universal';
import { React, ReactDOM, _ } from '../../lib';
import { Modal, ModalAnchored } from './modal';
import { stubDiv } from './stubs';
export const HelpIcon = (props) => (React.createElement(HelpTooltip, { title: props.title, timeout: 200 },
    React.createElement("span", { tabIndex: -1, className: BuildClass(['ui5-help-icon', props.className]) },
        React.createElement("img", { alt: "Help", className: BuildClass(['inline', props.classNameIcon]), src: "/static/img/svg/small-help-icon.svg" }))));
export const HelpTooltip = (props) => {
    const elementRef = React.useRef(stubDiv);
    const hoverTimeoutRef = React.useRef(null);
    const dimensionsRef = React.useRef({ width: 0, height: 0 });
    const testBoxRef = React.useRef(stubDiv);
    const [active, setActive] = React.useState(false);
    const [visible, setVisible] = React.useState(false);
    const contentStyle = props.contentOnly
        ? { background: 'transparent', borderRadius: 0, padding: 0 }
        : undefined;
    const [, setInc] = React.useState(0);
    React.useEffect(() => {
        if (!active) {
            return;
        }
        const animationFrame = window.requestAnimationFrame(() => {
            const el = testBoxRef.current;
            if (!el.isConnected) {
                return;
            }
            const box = el.getBoundingClientRect();
            const newSize = { width: box.width, height: box.height };
            if (!_.isEqual(newSize, dimensionsRef.current)) {
                dimensionsRef.current = { width: box.width, height: box.height };
                setInc(v => v + 1);
            }
        });
        return () => {
            window.cancelAnimationFrame(animationFrame);
        };
    }, [active, props.title]);
    const showTooltip = React.useCallback(() => {
        if (hoverTimeoutRef.current != null) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setActive(true);
        hoverTimeoutRef.current = timer(props.timeout ?? 500, () => {
            hoverTimeoutRef.current = null;
            setVisible(true);
        });
    }, [props.timeout]);
    const hideTooltip = React.useCallback(() => {
        if (hoverTimeoutRef.current != null) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setVisible(false);
        setActive(false);
    }, []);
    React.useEffect(() => {
        const mountElement = elementRef.current;
        mountElement.addEventListener('mouseenter', showTooltip);
        mountElement.addEventListener('mouseleave', hideTooltip);
        mountElement.addEventListener('focusin', showTooltip);
        mountElement.addEventListener('focusout', hideTooltip);
        return () => {
            mountElement.removeEventListener('mouseenter', showTooltip);
            mountElement.removeEventListener('mouseleave', hideTooltip);
            mountElement.removeEventListener('focusin', showTooltip);
            mountElement.removeEventListener('focusout', hideTooltip);
            if (hoverTimeoutRef.current != null) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
            }
        };
    }, [hideTooltip, showTooltip]);
    React.useEffect(() => {
        if (!active) {
            return;
        }
        document.addEventListener('scroll', hideTooltip, true);
        window.addEventListener('resize', hideTooltip);
        return () => {
            document.removeEventListener('scroll', hideTooltip, true);
            window.removeEventListener('resize', hideTooltip);
        };
    }, [active, hideTooltip]);
    return (React.createElement(React.Fragment, null,
        React.cloneElement(props.children, { ref: elementRef }),
        active ? (React.createElement(React.Fragment, null,
            React.createElement(ModalAnchored, { mountElement: elementRef.current, hidden: !visible, width: dimensionsRef.current.width, maxHeight: dimensionsRef.current.height, className: "ui5-help-tooltip", alignX: "center", flipByDefault: props.placement !== 'bottom', adjacent: props.adjacent },
                React.createElement("div", { className: "inner", style: contentStyle }, props.title)),
            React.createElement(Modal, null,
                React.createElement("div", { className: "ui5-help-tooltip", ref: testBoxRef, "aria-hidden": true },
                    React.createElement("div", { className: "inner", style: contentStyle }, props.title))))) : null));
};
export const SimpleHelpTooltip = ({ children, tip, direction = 'top', offsetX = 0, offsetY = 0, }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const triggerRef = React.useRef(stubDiv);
    const tooltipRef = React.useRef(stubDiv);
    React.useEffect(() => {
        if (isVisible && triggerRef.current && tooltipRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            let top = 0;
            let left = 0;
            switch (direction) {
                case 'top':
                    top = triggerRect.top - tooltipRect.height - 10 + offsetY;
                    left = triggerRect.left + triggerRect.width / 2 + offsetX;
                    break;
                case 'bottom':
                    top = triggerRect.bottom + 10 + offsetY;
                    left = triggerRect.left + triggerRect.width / 2 + offsetX;
                    break;
                case 'left':
                    top = triggerRect.top + triggerRect.height / 2 + offsetY;
                    left = triggerRect.left - tooltipRect.width - 10 + offsetX;
                    break;
                case 'right':
                    top = triggerRect.top + triggerRect.height / 2 + offsetY;
                    left = triggerRect.right + 10 + offsetX;
                    break;
            }
            setPosition({
                top: top + window.scrollY,
                left: left + window.scrollX,
            });
        }
    }, [isVisible, direction, offsetX, offsetY]);
    const getTransform = () => {
        switch (direction) {
            case 'top':
            case 'bottom':
                return 'translateX(-50%)';
            case 'left':
            case 'right':
                return 'translateY(-50%)';
        }
    };
    const uiRoot = document.querySelector('.ui-root');
    if (!uiRoot) {
        throw new Error('ui-root not found');
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: triggerRef, onMouseEnter: () => {
                setIsVisible(true);
            }, onMouseLeave: () => {
                setIsVisible(false);
            } }, children),
        isVisible &&
            ReactDOM.createPortal(React.createElement("div", { className: "tailwind-wrapper" },
                React.createElement("div", { ref: tooltipRef, className: "absolute z-20 px-3 py-1 text-sm text-white bg-gray-800 rounded-md max-w-[200px] w-max break-words", style: {
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        transform: getTransform(),
                    } }, tip)), uiRoot)));
};
//# sourceMappingURL=help.js.map